import axios from "axios";
import UserService from "rc-app/services/UserService";

/* ── Share-link API client (matches the spec at
   docs/product specs/SHAREABLE_PROJECT_LINKS_SPEC.md) ──
   Backend live as of 2026-06-08 on Nivedita's tunnel. Three endpoints:
   - POST /share-links         → mint / fetch an existing code (auth)
   - POST /share-links/click   → record a click (PUBLIC, no auth)
   - GET  /share-links/my-stats → sharer's funnel dashboard (auth)
*/

const BASE = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/share-links`;

const authHeaders = () => ({
  Authorization: `Bearer ${UserService.getToken()}`,
  "ngrok-skip-browser-warning": "disabled",
  "Content-Type": "application/json",
});

const publicHeaders = () => ({
  "ngrok-skip-browser-warning": "disabled",
  "Content-Type": "application/json",
});

/* ── Environment-aware share_url rewrite ──
   Backend hardcodes the host (currently `openskyvr.com`). When we're on a
   non-prod agent-app host (local / staging-buffer / develop / netlify
   preview), rewrite the host to `stage.openskyvr.com` so shared links
   stay inside the same environment they were minted from. Prod hosts
   pass through unchanged. */
const PROD_AGENT_HOSTS = new Set([
  "openskyvr.com",
  "www.openskyvr.com",
  "agent.opensky.co.in",
]);

const rewriteShareUrlForEnv = (url) => {
  if (!url) return url;
  try {
    const host = (typeof window !== "undefined" && window.location?.hostname) || "";
    const isProd = PROD_AGENT_HOSTS.has(host);
    if (isProd) {
      // Already on prod — make sure the URL is on the prod host (defensive
      // in case the backend ever returns a stage URL).
      return url.replace(/^https?:\/\/stage\.openskyvr\.com/i, "https://openskyvr.com");
    }
    // Non-prod agent app → rewrite share host to stage so the link works
    // inside the same data env (stage Keycloak, stage backend, etc.).
    return url.replace(/^https?:\/\/(?:www\.)?openskyvr\.com/i, "https://stage.openskyvr.com");
  } catch {
    return url;
  }
};

/**
 * Mint (or fetch the existing idempotent) share link for a project.
 * Backend reads the sharer from the JWT — we only send project_id.
 * @param {string} projectId
 * @returns {{ share_code: string, share_url: string }}
 */
export const createShareLink = async (projectId) => {
  const r = await axios.post(BASE, { project_id: projectId }, { headers: authHeaders() });
  const data = r.data || {};
  // Backend hardcodes openskyvr.com in share_url — rewrite to the host that
  // matches our current agent-app env so stage links don't point at prod.
  if (data.share_url) data.share_url = rewriteShareUrlForEnv(data.share_url);
  return data;
};

/**
 * Record a viewer click. Public — no JWT required. Backend derives
 * ip / user_agent / is_bot / is_self_click from request headers + token
 * (if any). Frontend only sends fields it legitimately knows.
 * Response includes sharer + agency context so the EntryLoader can
 * render "Shared by your agent X" + the agency logo on first paint
 * (extension shipped 2026-06-XX alongside §5.10).
 * @param {{ share_code: string, src?: string, referrer?: string, utm_source?: string, utm_medium?: string, utm_campaign?: string }} payload
 * @returns {{ ok: boolean, click_id?: string, project_id: string, project_slug: string, project_name?: string, agency_slug: string, agency_name?: string, agency_logo_url?: string, sharer_name?: string, sharer_roles_in_agency?: string[] }}
 */
export const recordShareLinkClick = async (payload) => {
  const r = await axios.post(`${BASE}/click`, payload, { headers: publicHeaders() });
  return r.data;
};

/**
 * Anonymous public project fetch. No auth required.
 * Per SHAREABLE_PROJECT_LINKS_SPEC §5.10 — returns a whitelisted project
 * doc with meta_models / matterports / resolved amenities all inlined,
 * so one round-trip gives V3 everything it needs to render in public-
 * viewer mode.
 * @param {string} projectId
 * The Promise<> wrapper is load-bearing, not decoration: without it
 * TypeScript infers the checked JS return type as the object itself and
 * every `.then()` at a call site becomes a type error.
 * @returns {Promise<{ _id, project_id, slug, project_name, description, address, geo_location, logo, project_images, project_brochure, property_videos, route_view, project_type_details, project_area, saleable_area, completion_date, launch_date, registration_number, agency_public, amenities, meta_models, matterports }>}
 */
export const getPublicProject = async (projectId) => {
  const url = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/public/projects/${projectId}`;
  const r = await axios.get(url, { headers: publicHeaders() });
  return r.data;
};

/**
 * Sharer's funnel dashboard data. Auth required. Optional date range —
 * per SHAREABLE_PROJECT_LINKS_SPEC §5.5, backend accepts `from` + `to`
 * as YYYY-MM-DD strings to scope click/session/lead counts to a window.
 * Pass null for "lifetime".
 * @param {{ from?: string|null, to?: string|null }} [opts]
 * @returns {{ shares: Array<{ share_code, project_id, project_name, share_url, total_clicks, unique_clicks, vr_sessions, leads, conversion_rate }> }}
 */
export const getMyShareLinkStats = async (opts = {}) => {
  const params = {};
  if (opts.from) params.from = opts.from;
  if (opts.to) params.to = opts.to;
  const r = await axios.get(`${BASE}/my-stats`, {
    headers: authHeaders(),
    params,
  });
  return r.data;
};

/**
 * Agency-wide share links (manager view). Auth required. Returns every
 * share link minted under the caller's agency with per-link counts and the
 * sharer's identity — the join key that lets Share Analytics attribute
 * clicks/sessions/leads to a person (and, via the in-memory role maps, a
 * role). Response shape is parsed defensively in the page because the field
 * naming on the new endpoint isn't locked yet; one row roughly:
 *   { share_code, project_id, user_id|sharer_id, total_clicks, unique_clicks,
 *     vr_sessions, leads, ... }
 * @returns {{ shares?: any[] } | any[]}
 */
export const getAgencyShareLinks = async () => {
  const r = await axios.get(BASE, {
    headers: authHeaders(),
    params: { scope: "agency" },
  });
  return r.data;
};

/**
 * Agency-wide raw click log (manager view). Auth required. Used as a
 * fallback source for Clicks / unique Visitors when the per-link aggregate
 * above doesn't carry them. Parsed defensively.
 * @returns {{ clicks?: any[] } | any[]}
 */
export const getAgencyShareLinkClicks = async (page = 1, limit = 200) => {
  // The endpoint HARD-CAPS the page at 200 docs regardless of `limit`, and
  // paginates via `page` (1-indexed) — `skip`/`offset` are silently ignored
  // (verified against the live API). So callers must page through with `page`
  // to get the full log; AnalyticsV3 loops until a short page. Without this the
  // Clicks/Visitors counts silently under-report past 200. JWT scopes the
  // result to the caller's agency — no scope param exists on this endpoint.
  const r = await axios.get(`${BASE}/clicks`, {
    headers: authHeaders(),
    params: { limit, page },
  });
  return r.data;
};

/**
 * Resolve a user's role within an agency (email → role). Auth required.
 * Used to attribute share links to a role tier (the agency links endpoint
 * only carries the sharer's email/uuid, not their role). Param + response
 * shapes parsed defensively until confirmed against the live endpoint.
 * @param {string} email
 * @param {string} agencySlug
 * @returns {any}
 */
export const getUserRoleInAgency = async (email, agencySlug) => {
  const base = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/views/user/roles`;
  const r = await axios.get(base, {
    headers: authHeaders(),
    params: { email_id: email, agency_slug: agencySlug },
  });
  return r.data;
};
